import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { isEqual } from 'lodash';
import type { Dispatch, SetStateAction } from 'react';
import type { SnackbarAction } from 'notistack';

import type { Id } from '@e-socna/data-layer';

import { NotiStackButton } from '../../../components/NotiStackButton';
import { PATH_NOT_FOUND } from '../../../app/constants/paths.constant';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { memoForm2ApiMapper } from '../../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import type { EditorModePaths } from '../../../components/DocumentEditor/types/props.type';
import type { MemosState } from '../../../forms/MemoBeforeSubmit';
import type { BackError } from '../../../utils/back-errors.util';

import { quotationForm2ApiMapper } from '../mappers/quotation.form2api.mapper';
import { quotationInfoForm2ApiMapper } from '../mappers/quotation-info.form2api.mapper';
import type { MissionQuotationWithItsInfo } from '../types/mission-quotation-with-its-info.type';
import type { MissionQuotationPage_QuotationInfo } from '../fragments/quotation-info.fragment';
import { MessageList } from '../../../components/MessageList';

import { useFoapuMissionQuotationMutation } from './useUpdateMutation.hook';
import { useUpdateQuotationInfoMutation } from './useUpdateInfoMutation.hook';
import { useQueryInvalidation } from './useQueryInvalidation.hook';
import type { MissionQuotation_Foapu_Result } from './useUpdateMutation.hook';

interface Params {
  readonly currentInfo?: MissionQuotationPage_QuotationInfo | null;
  readonly editorModePaths: EditorModePaths;
  readonly missionQuotationId: Id;
  readonly setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const useSaveHandler = ({
  currentInfo,
  editorModePaths,
  missionQuotationId,
  setIsLoading,
}: Readonly<Params>) => {
  const { invalidateQueries, invalidateMissionQueries } =
    useQueryInvalidation(missionQuotationId);

  const { missionId } = useMissionReadyContext();

  const { mutate: updateQuotationInfoMutation } =
    useUpdateQuotationInfoMutation();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const { mutate: updateMissionQuotationMutation } =
    useFoapuMissionQuotationMutation();

  const onSave = useCallback(
    (
      values: MissionQuotationWithItsInfo,
      memos: MemosState,
      handleSuccess?: (data: MissionQuotation_Foapu_Result) => void,
      handleError?: () => void,
      // eslint-disable-next-line max-params
    ) => {
      setIsLoading(true);

      updateMissionQuotationMutation(
        {
          filter: { _id: missionQuotationId },
          update: quotationForm2ApiMapper(values),
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError: (error: unknown) => {
            setIsLoading(false);
            const backError = error as BackError;
            let action: SnackbarAction | null = null;

            const supportedErrors = [
              'NO_CODE_OR_LABEL',
              'CODE_ALREADY_USED',
              'EMPTY_SECTION',
            ];

            const supportedErrorCode = backError.response.errors.find(
              (error) =>
                error.extensions.errCode &&
                supportedErrors.includes(error.extensions.errCode),
            )?.extensions.errCode;

            if (supportedErrorCode) {
              let to = editorModePaths.Editor;

              switch (supportedErrorCode) {
                case 'NO_CODE_OR_LABEL':
                  to = editorModePaths.InfoEdit;
                  break;
                case 'CODE_ALREADY_USED':
                  to = editorModePaths.InfoEdit;
                  break;
                case 'EMPTY_SECTION':
                  to = editorModePaths.Editor;
                  break;
                default:
                  console.warn(
                    `Unknown error code in MissionQuotation useSaveHandler: ${supportedErrorCode}`,
                  );
              }

              action = (snackbarId) => (
                <NotiStackButton
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={() => {
                    void navigate(to ?? PATH_NOT_FOUND);
                    closeSnackbar(snackbarId);
                  }}
                  variant="text"
                >
                  Modifier
                </NotiStackButton>
              );
            }

            enqueueSnackbar(
              <MessageList messages={userFriendlyErrors(error as BackError)} />,
              {
                variant: 'error',
                action,
              },
            );

            handleError?.();
          },
          onSuccess: (data: MissionQuotation_Foapu_Result) => {
            setIsLoading(false);

            enqueueSnackbar('Votre devis a bien été mis à jour.');

            invalidateQueries();

            handleSuccess?.(data);
          },
        },
      );

      if (!isEqual(currentInfo, values.info)) {
        updateQuotationInfoMutation(
          {
            missionId,
            update: quotationInfoForm2ApiMapper(values.info),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError: (error: unknown) => {
              enqueueSnackbar(
                <MessageList
                  messages={userFriendlyErrors(error as BackError)}
                />,
                {
                  variant: 'error',
                },
              );
            },
            onSuccess: () => {
              enqueueSnackbar(
                'Les informations liées au devis ont été mises à jour.',
              );

              invalidateMissionQueries();
            },
          },
        );
      }
    },
    [
      closeSnackbar,
      currentInfo,
      editorModePaths.Editor,
      editorModePaths.InfoEdit,
      enqueueSnackbar,
      invalidateQueries,
      invalidateMissionQueries,
      missionId,
      missionQuotationId,
      navigate,
      setIsLoading,
      updateMissionQuotationMutation,
      updateQuotationInfoMutation,
    ],
  );

  return { onSave };
};
